import React, { useState, useEffect, useCallback, memo } from 'react';
import { Box, Typography, TextField, Button, IconButton, CircularProgress, Select, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { inputStyles, getInputStyles } from './styles';
import { validateCertifications } from './validation';
import { MONTHS, YEAR_RANGE, parseDateString } from './constants';

const CertificationsSection = memo(({ initialData, authAxios, setError, onUpdate }) => {
    const [certifications, setCertifications] = useState(initialData || []);
    const [saving, setSaving] = useState(false);
    const [showErrors, setShowErrors] = useState(true);

    useEffect(() => {
        if (initialData) {
            // Parse dates from API (YYYY-MM-DD) to month/year components
            const parsedData = initialData.map(cert => {
                const issueParsed = parseDateString(cert.issue_date);
                const expiryParsed = parseDateString(cert.expiry_date);
                return {
                    ...cert,
                    issue_month: issueParsed.month,
                    issue_year: issueParsed.year,
                    expiry_month: expiryParsed.month,
                    expiry_year: expiryParsed.year,
                };
            });
            setCertifications(parsedData);
        }
    }, [initialData]);

    const addCertification = useCallback(() => {
        setCertifications(prev => [...prev, {
            id: Date.now(), name: '', issuing_organization: '', 
            issue_month: '', issue_year: '', expiry_month: '', expiry_year: '',
            credential_id: '', credential_url: '',
        }]);
    }, []);

    const updateCertification = useCallback((id, field, value) => {
        setCertifications(prev => prev.map(cert => cert.id === id ? { ...cert, [field]: value } : cert));
    }, []);

    const removeCertification = useCallback(async (id) => {
        if (typeof id === 'number' && id > 1000000000000) {
            setCertifications(prev => prev.filter(cert => cert.id !== id));
            return;
        }
        try {
            await authAxios.delete(`/certifications/${id}/`);
            setCertifications(prev => prev.filter(cert => cert.id !== id));
        } catch (error) {
            setError?.('Failed to remove certification');
        }
    }, [authAxios, setError]);

    // Format date for API (YYYY-MM-DD)
    const formatDate = (month, year) => {
        if (!month || !year) return null;
        return `${year}-${month.padStart(2, '0')}-01`;
    };

    const handleSaveAll = async () => {
        const errors = validateCertifications(certifications);
        if (errors.length > 0) {
            setShowErrors(true);
            setError?.('Please fill in required fields for all certifications');
            return;
        }

        setSaving(true);
        try {
            const savedEntries = await Promise.all(certifications.map(async cert => {
                const payload = {
                    name: cert.name, issuing_organization: cert.issuing_organization,
                    issue_date: formatDate(cert.issue_month, cert.issue_year),
                    expiry_date: formatDate(cert.expiry_month, cert.expiry_year),
                    credential_id: cert.credential_id, credential_url: cert.credential_url,
                };
                if (typeof cert.id === 'number' && cert.id > 1000000000000) {
                    return (await authAxios.post('/certifications/', payload)).data;
                }
                return (await authAxios.patch(`/certifications/${cert.id}/`, payload)).data;
            }));
            // Parse dates from response
            const parsedEntries = savedEntries.map(cert => {
                const issueParsed = parseDateString(cert.issue_date);
                const expiryParsed = parseDateString(cert.expiry_date);
                return {
                    ...cert,
                    issue_month: issueParsed.month,
                    issue_year: issueParsed.year,
                    expiry_month: expiryParsed.month,
                    expiry_year: expiryParsed.year,
                };
            });
            setCertifications(parsedEntries);
            onUpdate?.(savedEntries);
        } catch (error) {
            setError?.('Failed to save certifications');
        } finally {
            setSaving(false);
        }
    };

    const hasFieldError = (idx, field) => showErrors && field === 'name' && !certifications[idx]?.name?.trim();

    // Dropdown styles
    const selectStyles = {
        borderRadius: '6px',
        fontSize: '0.875rem',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d1d5db',
        },
    };

    return (
        <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>Certifications</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={addCertification} startIcon={<AddIcon />} sx={{ color: '#4f46e5', textTransform: 'none', fontWeight: 500 }}>Add Certification</Button>
                    <Button variant="contained" onClick={handleSaveAll} disabled={saving || certifications.length === 0}
                        sx={{ bgcolor: '#4f46e5', color: 'white', textTransform: 'none', fontWeight: 500, borderRadius: '6px', px: 3, '&:hover': { bgcolor: '#4338ca' } }}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : 'Save All'}
                    </Button>
                </Box>
            </Box>
            {certifications.length === 0 ? (
                <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>No certifications added.</Typography>
            ) : certifications.map((cert, index) => (
                <Box key={cert.id} sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', p: 3, mb: 2, position: 'relative' }}>
                    <IconButton size="small" onClick={() => removeCertification(cert.id)} sx={{ position: 'absolute', top: 8, right: 8 }}><CloseIcon fontSize="small" /></IconButton>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>Name<span style={{ color: '#4f46e5', marginLeft: '2px' }}>*</span></Typography>
                            <TextField fullWidth size="small" placeholder="Certification name" value={cert.name || ''}
                                onChange={(e) => updateCertification(cert.id, 'name', e.target.value)} sx={hasFieldError(index, 'name') ? getInputStyles(true, true) : inputStyles} />
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>Issuing Organization</Typography>
                            <TextField fullWidth size="small" placeholder="Organization" value={cert.issuing_organization || ''}
                                onChange={(e) => updateCertification(cert.id, 'issuing_organization', e.target.value)} sx={inputStyles} />
                        </Box>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>Issue Date</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 1 }}>
                                <Select
                                    size="small"
                                    value={cert.issue_month || ''}
                                    onChange={(e) => updateCertification(cert.id, 'issue_month', e.target.value)}
                                    displayEmpty
                                    IconComponent={ExpandMoreIcon}
                                    sx={selectStyles}
                                >
                                    <MenuItem value="">Month</MenuItem>
                                    {MONTHS.map(m => (
                                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                    ))}
                                </Select>
                                <Select
                                    size="small"
                                    value={cert.issue_year || ''}
                                    onChange={(e) => updateCertification(cert.id, 'issue_year', e.target.value)}
                                    displayEmpty
                                    IconComponent={ExpandMoreIcon}
                                    sx={selectStyles}
                                >
                                    <MenuItem value="">Year</MenuItem>
                                    {YEAR_RANGE.map(y => (
                                        <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>Expiry Date</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 1 }}>
                                <Select
                                    size="small"
                                    value={cert.expiry_month || ''}
                                    onChange={(e) => updateCertification(cert.id, 'expiry_month', e.target.value)}
                                    displayEmpty
                                    IconComponent={ExpandMoreIcon}
                                    sx={selectStyles}
                                >
                                    <MenuItem value="">Month</MenuItem>
                                    {MONTHS.map(m => (
                                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                    ))}
                                </Select>
                                <Select
                                    size="small"
                                    value={cert.expiry_year || ''}
                                    onChange={(e) => updateCertification(cert.id, 'expiry_year', e.target.value)}
                                    displayEmpty
                                    IconComponent={ExpandMoreIcon}
                                    sx={selectStyles}
                                >
                                    <MenuItem value="">Year</MenuItem>
                                    {YEAR_RANGE.map(y => (
                                        <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>Credential ID</Typography>
                            <TextField fullWidth size="small" placeholder="Credential ID" value={cert.credential_id || ''}
                                onChange={(e) => updateCertification(cert.id, 'credential_id', e.target.value)} sx={inputStyles} />
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>Credential URL</Typography>
                            <TextField fullWidth size="small" placeholder="https://..." value={cert.credential_url || ''}
                                onChange={(e) => updateCertification(cert.id, 'credential_url', e.target.value)} sx={inputStyles} />
                        </Box>
                    </Box>
                </Box>
            ))}
        </Box>
    );
});

CertificationsSection.displayName = 'CertificationsSection';
export default CertificationsSection;
